"use strict";

import angular from "angular";

import "./themes/visualization-nps-dark.component.scss";
import "./themes/visualization-nps.component.scss";
import template from "./visualization-nps.component.html";

import _ from "lodash";

import { NPSGateway } from "../../../gateway/NPSGateway/NPSGateway";
import { NPSDataFormat } from "../../../presentation/adpters/NPSDataFormat/NPSDataFormat";
import { CalculatingMachine } from "../../../presentation/services/CalculatingMachine/CalculatingMachine";
import { ColorService } from "../../../presentation/services/ColorService/ColorService";
import { FormatUnits } from "../../../presentation/services/FormatUnits/FormatUnits";

class VisualizationNPSComponent {
    constructor(
        $scope,
        $filter,
        $rootScope,
        $stateParams,
        LoaderService,
        ThemesService
    ) {
        this.$scope = $scope;
        this.$filter = $filter;
        this.$rootScope = $rootScope;
        this.$stateParams = $stateParams;
        this.LoaderService = LoaderService;
        this.ThemesService = ThemesService;

        this.accountingId = this.$stateParams.accountingId;

        this.CalculatingMachine = new CalculatingMachine();
        this.FormatUnits = new FormatUnits($filter);
        this.NPSGateway = new NPSGateway(this.accountingId);
        this.NPSDataFormat = new NPSDataFormat({
            NPSGateway: this.NPSGateway
        });
        this.ColorService = new ColorService({
            ThemesService: this.ThemesService
        });

        this.NPSDataFormat.setAccountingId(this.$stateParams.accountingId);

        this.loading = true;
        this.recommendations = [];
        this.series = [];
        this.seriesComparation = [];
        this.points = [];

        this.period = {};

        const deregisterThemesService = this.$rootScope.$on(
            "THEMES_SERVICE_THEME_CHANGED",
            (event, theme) => {
                this.loading = true;

                this.ColorService.setColorsChart();

                this._setCustomPointStructure();

                setTimeout(() => {
                    this.loading = false;
                    this.$scope.$applyAsync();
                }, 1000);
            }
        );

        this.$scope.$on("$destroy", () => {
            deregisterThemesService();
        });
    }

    $onInit() {
        this.period = angular.copy(this.currentPeriod);

        this._setData();
    }

    $onChanges(changes) {
        if (_.get(changes, "currentPeriod.previousValue.date"))
            this._setData(false);
    }

    _setCustomPointStructure() {
        if (!this.visualization.customStructure) return;

        const categories = {
            gauge: () => {
                const lastPoint = _.last(this.points);

                this.visualization.chart.series[0].data[0] = Number(
                    this.FormatUnits.formatValue(
                        "int",
                        _.get(lastPoint, "m.v", 0),
                        0
                    )
                );

                const plotBands = _.get(
                    this.visualization,
                    "chart.yAxis.plotBands"
                );

                if (plotBands) {
                    this.visualization.chart.yAxis.plotBands = plotBands.map(
                        (p) => {
                            p.color = this.ColorService.colorByPath(
                                p.colorPath
                            );

                            return p;
                        }
                    );
                }
            }
        };
        const category = _.get(this.visualization, "chart.chart.type", "");

        if (categories[category]) categories[category]();
    }

    _setData(loading = true) {
        this.LoaderService.setProgress(true);
        this.loading = true;
        if (this.visualization.special) this._setTableData();
        else this._setPoints(this.currentPeriod);
        this._setCustomPointStructure();
        this.period = angular.copy(this.currentPeriod);
        this._setComparationValues();
        this.loading = false;
        this.LoaderService.setProgress(false);
        this.$scope.$applyAsync();
    }

    _setTableData() {
        this.recommendations = this.npsData?.getRecommendations() || [];
        this.$scope.$applyAsync();
    }

    _setPoints(currentPeriod) {
        this.points = this.NPSDataFormat.getPoints({
            chart: this.visualization._id,
            series: this.visualization.series,
            year: currentPeriod.date.getUTCFullYear(),
            quarter: (currentPeriod.date.getUTCMonth() + 1) / 3
        });
    }

    _setComparationValues() {
        const seriesComparation = this.visualization.seriesProps.filter(
            (s) => s.comparation
        );

        this.seriesComparation = seriesComparation.map((serie) => {
            const points = this.points.filter((p) => p.s === serie._id);

            const currentMonth = String(
                this.currentPeriod.date.getUTCMonth() + 1
            ).padStart(2, "0");

            const currentPoint = _.findLast(points, (p) => {
                return (
                    `${p.t.getUTCFullYear()}${String(
                        p.t.getUTCMonth() + 1
                    ).padStart(2, "0")}` ===
                    `${this.currentPeriod.date.getUTCFullYear()}${currentMonth}`
                );
            });

            const comparationMonth = String(
                this.currentComparation.date.getUTCMonth() + 1
            ).padStart(2, "0");

            const comparationPoint = _.findLast(points, (p) => {
                return (
                    `${p.t.getUTCFullYear()}${String(
                        p.t.getUTCMonth() + 1
                    ).padStart(2, "0")}` ===
                    `${this.currentComparation.date.getUTCFullYear()}${comparationMonth}`
                );
            });

            const _pointValueX = _.get(currentPoint, "m.v") || 0;
            const _pointValueY = _.get(comparationPoint, "m.v") || 0;

            const value = _pointValueX - _pointValueY;

            const percentage = value / _pointValueY;

            const variationCondition = this.CalculatingMachine.variationCondition(
                _pointValueX,
                _pointValueY,
                serie.polarity
            );

            const polarityColor = this.CalculatingMachine.polarityColor(
                {
                    polarity: serie.polarity,
                    current: _pointValueX,
                    prev: _pointValueY,
                    value
                },
                false,
                variationCondition
            );

            return {
                name: serie.name,
                comparationValue: this.FormatUnits.formatValue(
                    serie.unit,
                    _pointValueX,
                    "2"
                ),
                percentage: isNaN(percentage) ? 0 : percentage,
                value,
                polarityColor,
                variationCondition,
                tooltipValue: 0,
                hideName: serie.hideName,
                unit: serie.unit
            };
        });
    }
}

export { VisualizationNPSComponent };

VisualizationNPSComponent.$inject = [
    "$scope",
    "$filter",
    "$rootScope",
    "$stateParams",
    "LoaderService",
    "ThemesService"
];

const name = "visualizationNpsComponent";
const def = {
    controller: VisualizationNPSComponent,
    bindings: {
        visualization: "<",
        currentPeriod: "<",
        currentComparation: "<",
        npsData: "<"
    },
    template
};
angular.module("nucont.shared").component(name, def);
export default { name, ...def };
